import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { includes, isEmpty } from "lodash";
import moment from "moment";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { employerConstants, userConstants } from "actions/types";
import {
  createSetupIntent,
  getPaymentRequirement,
  getSetupIntent,
  setInvoicePaymentMethod,
  skipBilling,
} from "actions/employerActions";
import {
  billingStartDateSelector,
  collectsPaymentMethodSelector,
  openInvoiceAmountDueSelector,
  planCodeSelector,
  requiresBillingDetails,
} from "store/selectors/employer";
import { formatCurrency } from "utils/number";

import Alert from "components/Alert";
import Button from "components/Button";
import ProgressBar from "components/ProgressBar";
import IconSpinner from "components/IconSpinner";
import StripeForm from "components/StripeForm";
import env from "@beam-australia/react-env";
import { getUserState } from "actions/userActions";
import PlanCard from "./PlanCard";
import { BILLING_INFO } from "statics/onboardingSteps";
import { push } from "store/actions/router";
import type { RootState } from "types/state";

const errorSelector = createErrorSelector([
  employerConstants.GET_SETUP_INTENT,
  employerConstants.CREATE_SETUP_INTENT,
  employerConstants.SKIP_BILLING,
  employerConstants.SET_INVOICE_PAYMENT_METHOD,
]);

const skippingSelector = createLoadingSelector([
  employerConstants.SKIP_BILLING,
  userConstants.USER_STATE,
]);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
    isMultiAccount: state.employer.isMultiAccount,
    planCode: planCodeSelector(state),
    isSkipping: skippingSelector(state),
    requiresBillingDetails: requiresBillingDetails(state),
    collectsPaymentMethod: collectsPaymentMethodSelector(state),
    openInvoiceAmountDue: openInvoiceAmountDueSelector(state),
    billingStartDate: billingStartDateSelector(state),
  };
};

const mapDispatchToProps = {
  getSetupIntent,
  getPaymentRequirement,
  createSetupIntent,
  setInvoicePaymentMethod,
  getUserState,
  skipBilling,
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  isDashboard?: boolean;
  onSuccess?: () => void;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  initialFetching: boolean;
  stripeClientSecret: string | null | undefined;
  loadFailed: boolean;
}

// Minimal shape of the Stripe-backed setup intent the billing flow reads.
interface SetupIntentLike {
  status?: string;
  client_secret?: string | null;
  payment_method?: unknown;
}

class EmployerBilling extends React.PureComponent<Props, State> {
  stripePromise = loadStripe(env("STRIPE_PUBLIC_KEY"));

  constructor(props: Props) {
    super(props);

    this.state = {
      initialFetching: true,
      stripeClientSecret: null,
      loadFailed: false,
    };
  }

  async componentDidMount() {
    if (!this.props.requiresBillingDetails) {
      this.props.push("/");
    }
    await this._load();
  }

  _load = async () => {
    let stripeClientSecret: string | null | undefined;
    const [intent, requirement] = (await Promise.all([
      this.props.getSetupIntent(this.props.client as ApolloClient<object>),
      this.props.getPaymentRequirement(
        this.props.client as ApolloClient<object>
      ),
    ])) as [
      { type: string; success: boolean; data?: SetupIntentLike },
      { type: string; success: boolean; data?: unknown },
    ];
    const { data } = intent;

    // without it an order company would be told it won't be charged and lose its skip, so nothing is offered until it loads
    if (!intent.success || !requirement.success || !requirement.data) {
      this.setState({ loadFailed: true, initialFetching: false });
      return;
    }

    /* if the intent has a status of succeeded it means the customer has provided a valid payment method and we should
     progress them forward. This acts as a catch if they submit billing info but refresh the page before we can make all
     the necessary api calls
     */
    const moveForwardStatuses = ["succeeded", "requires_action"];
    if (data && includes(moveForwardStatuses, data.status)) {
      if (!(await this._onSuccess(data))) {
        // the intent is already confirmed, so Try Again re-runs the save
        this.setState({ loadFailed: true, initialFetching: false });
        return;
      }
    } else if (isEmpty(data)) {
      const created = (await this.props.createSetupIntent(
        this.props.client as ApolloClient<object>
      )) as {
        type: string;
        success: boolean;
        data?: SetupIntentLike;
      };
      if (!created.success || !created.data) {
        this.setState({ loadFailed: true, initialFetching: false });
        return;
      }
      stripeClientSecret = created.data.client_secret;
    } else {
      stripeClientSecret = data.client_secret;
    }

    // if no setupIntent returned then we need to create one before starting the stripe process

    this.setState({
      stripeClientSecret,
      initialFetching: false,
    });
  };

  _retryLoad = () => {
    this.setState({ initialFetching: true, loadFailed: false });
    void this._load();
  };

  _onSuccess = async ({
    payment_method: paymentMethod,
    ...setupIntent
  }: SetupIntentLike): Promise<boolean> => {
    const requiresMicroDepositVerification =
      setupIntent.status === "requires_action";
    let succeeded = true;
    if (requiresMicroDepositVerification) {
      // fire state machine and deal with microdeposits later so user can get through KYB
      if (!this.props.isDashboard) {
        const skipped = await this.props.skipBilling(
          this.props.client as ApolloClient<object>
        );
        succeeded = skipped.success;
      }
    } else {
      const saved = await this.props.setInvoicePaymentMethod(
        this.props.client as ApolloClient<object>,
        paymentMethod as Parameters<
          typeof this.props.setInvoicePaymentMethod
        >[1]
      );
      succeeded = saved.success;
    }
    if (succeeded) {
      if (this.props.isDashboard) {
        this.props.onSuccess!();
        await this.props.getSetupIntent(
          this.props.client as ApolloClient<object>
        );
        // checking the requirement releases a held dashboard as soon as the payment method is saved
        await this.props.getPaymentRequirement(
          this.props.client as ApolloClient<object>
        );
        await this.props.getUserState(
          this.props.client as ApolloClient<object>
        );
      } else {
        await this.props.getUserState(
          this.props.client as ApolloClient<object>
        );
      }
    }
    return succeeded;
  };

  _getOrderChargeText = () => {
    if (this.props.billingStartDate) {
      return `You won't be charged until ${moment(
        this.props.billingStartDate
      ).format(
        "MMMM D, YYYY"
      )}, when your first invoice will be charged to it automatically.`;
    }
    if (this.props.openInvoiceAmountDue > 0) {
      return `Your open invoice of ${formatCurrency(
        this.props.openInvoiceAmountDue / 100
      )} will be charged right away when you save, and future invoices will be charged automatically.`;
    }
    return "Your invoices will be charged to it automatically, starting with your first invoice.";
  };

  // a deferred order's dashboard is never held, there's nothing owed until billing starts
  _getSetupLaterText = () =>
    this.props.billingStartDate
      ? "You can also skip this and add one later from your dashboard."
      : "If you set this up later, your dashboard will open once a payment method is added or your invoice is paid.";

  _getDashboardView = () => {
    return (
      <div id="employer-billing">
        {this.props.error && <Alert type="error" msg={this.props.error} />}
        {this.props.collectsPaymentMethod && (
          <p className="page-subtext">{this._getOrderChargeText()}</p>
        )}
        <Elements
          stripe={this.stripePromise}
          options={{
            // original passes the raw state value (string | null) through
            clientSecret: this.state.stripeClientSecret as string | undefined,
            appearance: {
              theme: "stripe",
              variables: {
                spacingUnit: "4px",
                colorPrimary: "#1b9aa9",
              },
            },
          }}
        >
          <StripeForm
            onSuccess={this._onSuccess as (...args: unknown[]) => void}
          />
        </Elements>
      </div>
    );
  };

  skipBilling = async () => {
    await this.props.skipBilling(this.props.client as ApolloClient<object>);
    await this.props.getUserState(this.props.client as ApolloClient<object>);
  };

  render() {
    if (this.state.initialFetching) {
      return <IconSpinner centered />;
    }

    if (this.state.loadFailed) {
      return (
        <div id="employer-billing">
          <Alert
            type="error"
            msg="We couldn't load your billing details. Please try again."
          />
          {this.props.error && <Alert type="error" msg={this.props.error} />}
          <Button
            name="action"
            btnLabel="Try Again"
            onClick={this._retryLoad}
          />
        </div>
      );
    }

    if (this.props.isDashboard) return this._getDashboardView();
    return (
      <div id="employer-billing">
        <ProgressBar
          isEmployer={true}
          activeStepId={BILLING_INFO.id}
          progressPercent={"90"}
          isMultiAccount={this.props.isMultiAccount}
          showBillingRoute={true}
        />
        <div className="mega-container">
          <div className="step-container is-active" data-circle-percent="100">
            {this.state.initialFetching && <IconSpinner centered />}
            {!this.state.initialFetching && (
              <section className="form-sec-2col">
                <section className="page-title-wrap">
                  <article className="text-cell">
                    <h1 className="page-title">Billing Details</h1>
                    {this.props.error && (
                      <Alert type="error" msg={this.props.error} />
                    )}
                    {this.props.collectsPaymentMethod ? (
                      <p className="page-subtext">
                        Add a card or bank account to pay your plan&apos;s
                        invoices. {this._getOrderChargeText()}{" "}
                        {this._getSetupLaterText()}
                      </p>
                    ) : (
                      <p className="page-subtext">
                        Please provide your billing details, you will not be
                        charged until your account has been fully opened.
                      </p>
                    )}
                    <Elements
                      stripe={this.stripePromise}
                      options={{
                        // original passes the raw state value (string | null) through
                        clientSecret: this.state.stripeClientSecret as
                          | string
                          | undefined,
                        appearance: {
                          theme: "stripe",
                          variables: {
                            spacingUnit: "4px",
                            colorPrimary: "#1b9aa9",
                          },
                        },
                      }}
                    >
                      <StripeForm
                        onSuccess={
                          this._onSuccess as (...args: unknown[]) => void
                        }
                        showSkipButton={this.props.collectsPaymentMethod}
                        onSkip={this.skipBilling}
                        skippingPayment={this.props.isSkipping}
                      />
                    </Elements>
                  </article>
                </section>
                <section>
                  <article className="plan-card-bottom">
                    <PlanCard planCode={this.props.planCode as string} />
                  </article>
                </section>
              </section>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default connector(withApollo<Props>(EmployerBilling));
